import { useEffect, useState } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { currency, countryFlag } from "@/lib/format";
import { Trophy, Medal, Award, Star } from "lucide-react";
import { toast } from "sonner";

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/leaderboard"); setRows(data); }
      catch (e) { toast.error(getApiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid="leaderboard-page">
      <PageHeader eyebrow="Performance" title="Partner Leaderboard"
        subtitle="Live ranking by revenue contribution, with auto-calculated tiers." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {rows.slice(0, 3).map((r, i) => (
          <PodiumCard key={r.partner_id} row={r} place={i + 1} />
        ))}
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
              <tr>
                <th className="px-5 py-3">Rank</th>
                <th className="px-5 py-3">Partner</th>
                <th className="px-5 py-3">Tier</th>
                <th className="px-5 py-3">Conversions</th>
                <th className="px-5 py-3">Conv. rate</th>
                <th className="px-5 py-3">Revenue</th>
                <th className="px-5 py-3">Progress to next</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.partner_id} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2]" data-testid={`leaderboard-row-${r.partner_id}`}>
                  <td className="px-5 py-3.5 text-sm font-bold text-[#2A4D3E]">#{r.rank}</td>
                  <td className="px-5 py-3.5">
                    <div className="text-sm font-medium text-[#2A332C]">{r.name}</div>
                    <div className="text-[11px] text-[#8E9992]">{r.organization} · {countryFlag(r.country)} {r.country}</div>
                  </td>
                  <td className="px-5 py-3.5"><TierBadge tier={r.tier} /></td>
                  <td className="px-5 py-3.5 text-sm text-[#2A332C]">{r.conversions} <span className="text-[10px] text-[#8E9992]">/ {r.total_leads}</span></td>
                  <td className="px-5 py-3.5 text-sm text-[#5C6660]">{r.conversion_rate}%</td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-[#2A332C]">{currency(r.revenue)}</td>
                  <td className="px-5 py-3.5 w-56">
                    {r.next_tier ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[#F2F4F3] rounded-full overflow-hidden">
                          <div className="h-full" style={{ width: `${r.progress_to_next}%`, background: r.tier.color }} />
                        </div>
                        <span className="text-[11px] text-[#8E9992] whitespace-nowrap">→ {r.next_tier.name}</span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-[#2A4D3E] font-semibold">Top tier</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PodiumCard({ row, place }) {
  const Icon = place === 1 ? Trophy : place === 2 ? Medal : Award;
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5 relative overflow-hidden" data-testid={`podium-${place}`}>
      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-10" style={{ background: row.tier?.color }} />
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: row.tier?.bg, color: row.tier?.color }}>
          <Icon size={17} strokeWidth={1.8} />
        </div>
        <div className="text-2xl font-heading font-bold text-[#2A332C]">#{place}</div>
      </div>
      <div className="font-medium text-[#2A332C]">{row.name}</div>
      <div className="text-[11px] text-[#8E9992]">{row.organization} · {countryFlag(row.country)} {row.country}</div>
      <div className="mt-3 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#8E9992] font-semibold">Revenue</div>
          <div className="font-heading text-xl font-semibold text-[#2A332C]">{currency(row.revenue)}</div>
        </div>
        <TierBadge tier={row.tier} />
      </div>
    </div>
  );
}

export function TierBadge({ tier }) {
  if (!tier) return null;
  return (
    <span data-testid={`tier-badge-${tier.id}`}
      style={{ background: tier.bg, color: tier.color }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
      <Star size={11} fill={tier.color} stroke="none" />
      {tier.name}
    </span>
  );
}
